/**
 * Full history — the whole pipeline that produced this contact list, as a
 * trail under the title: `ChopLink > Raw Import > AI review`.
 *
 * It replaces TWO things that each answered a slice of the question badly:
 * the breadcrumb (`Contact Lists / <this file>`, which never said anything a
 * recruiter did not already know) and `LineageChip` ("Derived from <parent>",
 * which reached exactly ONE ancestor).
 *
 * It lives behind a **history icon** beside the title (2026-08-26), not on a
 * line of its own. Provenance is a question the recruiter asks OCCASIONALLY —
 * "wait, where did this come from?" — and a permanent line for an occasional
 * question was pushing the facts they read every time further down the page.
 * The icon only renders when there is a history to show, so its mere presence
 * is the signal that this list came from somewhere.
 *
 * ## Click, not hover — and it has to be
 *
 * The first version used the shared `Tooltip`. It looked right and was unusable:
 * the trail's whole point is that the segments are LINKS, and a hover bubble
 * cannot be reached to click one. `Tooltip` separates trigger and bubble by
 * `mb-1.5`/`mt-1.5`, and that 6px gap belongs to neither element — crossing it
 * drops `group-hover` and the panel vanishes mid-reach. Even with the gap
 * closed, hover-to-click is a poor target on desktop and impossible on touch.
 *
 * So this is a real disclosure: click to open, click the button again, press
 * Escape, or click outside to close. Escape and outside-click are not polish —
 * without them a panel that opens on click is a panel with no way out that does
 * not involve guessing.
 *
 * The open/close/focus machinery lives in `IconPopover`, shared with the stats
 * popover, so the two cannot drift on the parts that are easy to get half-right
 * (returning focus to the trigger on Escape, `pointerdown` rather than `click`
 * for outside-close, one tab stop, the aria wiring).
 *
 * **The panel deliberately survives its own links.** `ImportDetailPage` mounts
 * this unkeyed, so clicking a segment navigates without unmounting: the popover
 * stays open and re-populates with the DESTINATION's trail. That is the useful
 * behaviour when walking a chain — the trail follows you instead of making you
 * re-open it at every hop. It is called out here because an earlier revision
 * closed the panel on link click and the reason for the change would otherwise
 * be invisible (`/pr-review` iter 2, Agent C).
 *
 * A HISTORY, not a breadcrumb, and the distinction is load-bearing —
 * `LineageChip` argued it first and was right: `Imports / parent / child`
 * reads as CONTAINMENT, and the child is not inside the parent, it was derived
 * from it. This sits below the title, is labelled "Full history", and uses `>`
 * as a "then" rather than a "within".
 *
 * ## Three rules, all from the product decision of 2026-08-25
 *
 * 1. **The first segment is the ORIGIN, and it is not a link.** It names the
 *    CHANNEL the pipeline started from, decided server-side off the root's
 *    `source`. There is no import to navigate to — "ChopLink" is where the
 *    data came from, not a row.
 * 2. **Show everything that is known.** A truncated chain renders truncated.
 *    There is no error copy, because a partial trail is not a failure.
 * 3. **No DATA, no line.** If the request fails or returns nothing, the block
 *    does not render at all — not an empty label, not a spinner. But a list
 *    with no ancestors DOES render: `Manual upload › Raw Import` answers "did
 *    this come from the extension or did someone upload a file", which is a
 *    real question on a raw list and one nothing else on the page answers.
 *
 * The type of each segment is derived with `deriveTypeTag`, the SAME function
 * the list pill and the detail badge use, from the `tags` + `import_format`
 * the endpoint ships. A second vocabulary here would let a segment disagree
 * with the badge on the page it links to.
 *
 * ## Combine & Filter (epic 2026-09-22, sub-task 05)
 *
 * A combined import has no `parent_import_id`, so `chain` is always empty for
 * one — `ImportLineage.sources` carries its provenance instead. Per that
 * schema's own docstring ("a client renders whichever of the two is
 * non-empty"), this component renders the chain when it is non-empty and
 * falls back to `CombinedFromBlock` (sources + the recipe summary) only when
 * `chain` is empty and `sources` is not. Rule 3 (no data, no trail) widens the
 * same way: nothing renders only when BOTH are empty, so every existing
 * chain-based test is unaffected.
 *
 * ## Expanding an entry in place
 *
 * Every row shows its contact count. A source that was itself built from
 * other lists, or a chain step that is a combine, gets a chevron (replacing
 * the `›` on chain rows) that unfolds what built it right here — see
 * `LineageExpander`. An ordinary chain step gets none: its parent is already
 * the row above it. The titles stay links. The panel scrolls rather than
 * growing past the viewport, since a combine of combines can nest deep.
 */
import { Link } from "react-router-dom";

import type { StoredCombineRecipe } from "@nu-assist/contracts";

import type { ImportsClient } from "../../../clients/imports.client";
import { formatLineageCount } from "../../../features/imports/selection/importLabels";
import { TAG_DEF_BY_VALUE, deriveTypeTag } from "../imports/tagDefs";
import { CombinedFromBlock } from "./CombinedFromBlock";
import { IconPopover } from "./IconPopover";
import { displayTitle } from "./importTitle";
import { type LineageExpandContext, LineageExpander, isCombineEntry } from "./LineageExpander";
import { useImportLineage } from "./useImportLineage";

export interface FullHistoryProps {
  client: ImportsClient;
  /** The import whose trail this is. */
  importId: string;
  /** `ImportDetail.combine_recipe` for THIS import — threaded through to
   *  `CombinedFromBlock`'s recipe summary. The lineage endpoint this
   *  component queries does not carry the recipe itself, only `sources`.
   *  `undefined`/`null` renders the source list with no recipe summary. */
  combineRecipe?: StoredCombineRecipe | null;
}

/** `2026-08-01T10:00:00Z` -> `Aug 1, 2026`. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** A segment's type def — label AND pill colours, from the same table the
 *  detail badge and the list pill read. A fourth spelling of "what colour is a
 *  Company Map" is exactly what that table exists to prevent. */
function typeDefOf(tags: string[] | undefined, importFormat: string | undefined) {
  const tag = deriveTypeTag(
    (tags ?? []) as Parameters<typeof deriveTypeTag>[0],
    importFormat as Parameters<typeof deriveTypeTag>[1]
  );
  return tag ? TAG_DEF_BY_VALUE[tag] : undefined;
}

export function FullHistory({ client, importId, combineRecipe }: FullHistoryProps) {
  const query = useImportLineage(importId, { client });

  const chain = query.data?.chain ?? [];
  const sources = query.data?.sources ?? [];

  // Rule 3, widened for a combined import (see the docblock's own section on
  // this). A one-entry chain still renders on its own: origin + this import
  // is the shortest true history, and on a raw list the ORIGIN is the whole
  // point.
  if (!query.data || (chain.length === 0 && sources.length === 0)) return null;

  // The popover's own rows are depth 0; this page's import is already "open".
  const expand: LineageExpandContext = { client, path: [importId], depth: 0 };

  return (
    <IconPopover
      label="Full history"
      triggerTestId="full-history-trigger"
      panelTestId="full-history"
      panelClassName="max-h-[70vh] max-w-[min(90vw,32rem)] overflow-y-auto"
      icon={
        /* Clock-with-arrow — the conventional "history" glyph. Inline SVG so
           it inherits `currentColor` and needs no icon dependency. */
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2.5 8a5.5 5.5 0 1 0 1.7-4" />
          <path d="M2 2v3h3" />
          <path d="M8 5v3l2 1.5" />
        </svg>
      }
    >
      <div className="mb-1.5 text-caption text-text-secondary">Full history</div>
      <ol className="flex flex-col gap-2">
        <li className="text-body-sm text-text-secondary">
          <span data-testid="full-history-origin">{query.data.origin}</span>
        </li>
        {chain.map((entry, i) => {
          const isCurrent = i === chain.length - 1;
          const def = typeDefOf(entry.tags, entry.import_format);
          const label = def?.label ?? "Import";
          return (
            // Keyed on id AND index: a cyclic chain (`A -> B -> A`) legitimately
            // repeats ids up to the depth cap, and React would warn on — and
            // mis-reconcile — duplicate keys (`/pr-review` iter 1, Agent C).
            <li key={`${entry.id}-${i}`}>
              <LineageExpander entry={entry} ctx={expand} eligible={isCombineEntry(entry)}>
                {(toggle) => (
                  <div className="flex items-start gap-1.5">
                    {toggle ?? (
                      <span aria-hidden="true" className="w-4 shrink-0 text-center text-text-muted">
                        ›
                      </span>
                    )}
                    {/* TWO lines per entry, never more: the type pill (and the
                        count beside it, never truncated) on top, one line of
                        provenance under it. The filename used to be a third
                        line and it is the longest string here — a five-step
                        chain became a wall. It rides the second line,
                        truncated, with the full value on `title`. */}
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="flex flex-wrap items-center gap-x-1.5">
                        {isCurrent ? (
                          <span
                            data-testid="full-history-current"
                            aria-current="page"
                            className={`inline-flex w-fit items-center rounded-pill px-2 py-0.5 text-caption ${
                              def?.pillClass ?? "bg-surface text-text-secondary"
                            }`}
                          >
                            {label}
                          </span>
                        ) : (
                          <Link
                            data-testid="full-history-link"
                            to={`/research/imports/${entry.id}`}
                            className={`inline-flex w-fit items-center rounded-pill px-2 py-0.5 text-caption hover:underline ${
                              def?.pillClass ?? "bg-surface text-text-secondary"
                            }`}
                          >
                            {label}
                          </Link>
                        )}
                        <span
                          data-testid="full-history-count"
                          className="whitespace-nowrap text-caption text-text-muted"
                        >
                          {formatLineageCount(entry)}
                        </span>
                      </span>
                      <span
                        className="truncate text-caption text-text-muted"
                        // The STORED name, not the stripped one — a `title` that
                        // repeats the visible text tells the reader nothing, and
                        // two hops differing only by extension would be
                        // identical (`/pr-review` iter 3, Agent C I4b).
                        title={entry.filename}
                      >
                        {formatDate(entry.created_at)} · {entry.run_by} ·{" "}
                        {displayTitle(entry.filename)}
                      </span>
                    </span>
                  </div>
                )}
              </LineageExpander>
            </li>
          );
        })}
      </ol>
      {/* A combined import: no `parent_import_id`, so `chain` is empty and
          `sources` carries the provenance instead (see the docblock's
          Combine & Filter section). */}
      {chain.length === 0 && sources.length > 0 && (
        <CombinedFromBlock sources={sources} recipe={combineRecipe ?? null} expand={expand} />
      )}
    </IconPopover>
  );
}
